import {
  appendActionCardChip,
  composerBody,
  resolveCardChatId,
} from '@littlebird/action-cards';
import { getStores, isDraftEmpty, MAIN_CHAT_MODE } from '@littlebird/sdk';
import { autorun, makeAutoObservable, reaction, runInAction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import { performAttributedSend } from '@/features/chat/chat-send';
import { isLiveThread } from '@/features/chat/live-thread';
import { logError } from '@/lib/errorLogging';
import { logger } from '@/lib/logger';

import {
  floating as floatingConfig,
  sidebar as sidebarConfig,
  storageKeys,
} from './context-chat.config';

import type {
  ActionCardChatTarget,
  ChatTarget,
  ContextChatMode,
  ContextChatSession,
  MeetingChatTarget,
  SessionContext,
} from './context-chat.types';

class ContextChatStore {
  sessions: ContextChatSession[] = [];
  activeSessionId: string | null = null;
  mode: ContextChatMode = 'collapsed';
  preferDocked = true;
  canDock = true;
  sidebarWidth = sidebarConfig.defaultWidth;
  floatingHeight = floatingConfig.defaultHeight;
  currentTarget: ChatTarget | undefined = undefined;
  composerOnly = false;
  dockOnly = false;
  // The page draws the chat inside its own layout, so the dock and the floating panel stay away
  embedded = false;
  private pendingAsk: {
    meetingId: string;
    content: string | null;
    isMeetingIntent?: boolean;
  } | null = null;
  private pendingCardAsk: { cardId: string; content: string } | null = null;
  // A card is asked once, so a double press sends once and later presses only reopen its chat
  private askedCardIds = new Set<string>();
  private collapsedByUser = false;
  private restoreAfterNavigation = false;
  private isSwitchingGeneralThread = false;
  private cardThreadLoads = new Map<string, 'loading' | 'missing'>();

  configure({ composerOnly }: { composerOnly: boolean }) {
    this.composerOnly = composerOnly;
  }

  get panelOpen(): boolean {
    return this.mode !== 'collapsed';
  }

  private get createsDraftOnTargetChange(): boolean {
    return (
      this.currentTarget?.type !== 'action-card' ||
      this.panelOpen ||
      this.restoreAfterNavigation
    );
  }

  constructor() {
    this.hydrate();
    makeAutoObservable(this, {}, { autoBind: true });
    autorun(() => this.persistChatLayout());
    autorun(() => this.pruneDeletedSessions());
    reaction(
      () => this.mode,
      (mode) => {
        if (mode !== 'collapsed') this.collapsedByUser = false;
      }
    );
    reaction(
      () => this.latestAssistantIdForFocusedSession(),
      (latestId) => {
        const focused = this.focusedSession;
        if (focused && latestId) focused.lastSeenAssistantId = latestId;
      },
      { fireImmediately: true }
    );
    reaction(
      () => {
        let threadsLoaded = false;
        let targetThreadId: string | null = null;
        try {
          const target = this.currentTarget;
          const stores = getStores();
          threadsLoaded = stores.threads.isLoaded;
          if (threadsLoaded && target) {
            targetThreadId = this.existingThreadId(target, stores);
          }
        } catch {
          threadsLoaded = false;
        }
        return [this.currentTarget, threadsLoaded, targetThreadId] as const;
      },
      () =>
        this.syncActiveSessionToTarget({
          createDraft: this.createsDraftOnTargetChange,
        }),
      { equals: (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2] }
    );
    reaction(
      () => this.mode === 'composer' && this.activeThreadHasMessages(),
      (shouldPromote) => {
        if (shouldPromote) this.openExpanded();
      }
    );
    reaction(
      () =>
        [
          this.activeSession?.threadId ?? null,
          this.activeThreadUserMessageCount(),
        ] as const,
      ([threadId, count], [previousThreadId, previousCount]) => {
        if (
          threadId &&
          threadId === previousThreadId &&
          count > previousCount &&
          this.mode === 'collapsed' &&
          !this.collapsedByUser
        ) {
          this.openExpanded();
        }
      }
    );
  }

  private activeThreadUserMessageCount(): number {
    const session = this.activeSession;
    if (!session) return 0;

    try {
      const { messages } = getStores();
      return messages
        .getMessagesByThreadId(session.threadId)
        .filter((message) => message.role === 'user').length;
    } catch {
      return 0;
    }
  }

  /**
   * Slack-thread-style auto-switch: whenever the page's target changes,
   * make the active session follow it. Reuses an existing session if
   * one already represents this target, otherwise materializes a new
   * session pointing at any existing meeting thread. Local draft threads
   * are only created when the panel is open/opening.
   */
  private syncActiveSessionToTarget({
    createDraft = false,
    target = this.currentTarget,
  }: { createDraft?: boolean; target?: ChatTarget } = {}) {
    if (!target) return;

    let stores: ReturnType<typeof getStores>;
    try {
      stores = getStores();
      if (!stores.threads?.isLoaded) return;
    } catch {
      return;
    }

    if (target.type === 'action-card') {
      const threadId = this.cardThreadId(target.cardId, stores);
      if (
        threadId &&
        !stores.threads.getById(threadId) &&
        this.cardThreadLoads.get(threadId) !== 'missing'
      ) {
        this.loadCardThread(threadId, stores);
        return;
      }
    }

    const previousSession = this.rawActiveSession;
    const existingThreadId = this.existingThreadId(target, stores);

    const matchingSession = this.sessions.find((s) =>
      this.sessionMatchesTarget(s, target)
    );

    let nextActiveSessionId: string;
    if (matchingSession) {
      if (
        existingThreadId &&
        existingThreadId !== matchingSession.threadId &&
        (target.type === 'action-card' ||
          stores.threads.isFreshDraftThread(matchingSession.threadId))
      ) {
        const previousThreadId = matchingSession.threadId;
        matchingSession.threadId = existingThreadId;
        matchingSession.lastSeenAssistantId =
          this.latestAssistantIdForThread(existingThreadId);
        this.discardLocalThreadIfOrphaned(previousThreadId);
      }
      nextActiveSessionId = matchingSession.id;
    } else {
      if (!existingThreadId && !createDraft) {
        if (previousSession) {
          this.activeSessionId = null;
          this.leaveSession(previousSession, stores);
        }
        return;
      }

      let context: SessionContext;
      let threadId: string;
      if (target.type === 'meeting') {
        context = { type: 'meeting', meetingId: target.meetingId };
        const meeting = stores.meetings.get(target.meetingId);
        threadId =
          existingThreadId ??
          stores.threads.createEmptyThread({
            meetingId: target.meetingId,
            projectId: meeting?.projectId ?? null,
            title: meeting?.name ?? '',
          }).id;
      } else if (target.type === 'action-card') {
        context = { type: 'action-card', cardId: target.cardId };
        threadId =
          existingThreadId ?? this.createCardThread(target.cardId, stores);
      } else {
        context = { type: 'general' };
        threadId = stores.threads.createEmptyThread({
          origin: 'meetings_overview',
        }).id;
      }
      const newSession: ContextChatSession = {
        id: uuidv4(),
        context,
        threadId,
        lastActiveAt: Date.now(),
        lastSeenAssistantId: this.latestAssistantIdForThread(threadId),
      };
      this.sessions.push(newSession);
      nextActiveSessionId = newSession.id;
    }

    if (this.activeSessionId !== nextActiveSessionId) {
      this.activeSessionId = nextActiveSessionId;

      if (previousSession && previousSession.id !== nextActiveSessionId)
        this.leaveSession(previousSession, stores);
    }

    if (this.restoreAfterNavigation) {
      this.restoreAfterNavigation = false;
      if (this.mode === 'collapsed') this.mode = this.expandedMode;
    }

    if (target.type === 'meeting') this.applyPendingAsk(target);
    if (target.type === 'action-card') this.applyPendingCardAsk(target);
  }

  private existingThreadId(
    target: ChatTarget,
    stores: ReturnType<typeof getStores>
  ): string | null {
    if (target.type === 'meeting')
      return stores.threads.getThreadForMeeting(target.meetingId)?.id ?? null;
    if (target.type !== 'action-card') return null;

    const threadId = this.cardThreadId(target.cardId, stores);
    return threadId && isLiveThread(stores.threads, threadId) ? threadId : null;
  }

  private cardThreadId(
    cardId: string,
    stores: ReturnType<typeof getStores>
  ): string | null {
    const card = stores.actionCards.get(cardId);
    if (!card) return null;
    return resolveCardChatId(card, {
      getCard: (id) => stores.actionCards.get(id),
      getChildren: (id) => stores.actionCards.getChildren(id),
      // Prefer loading the family's existing chat over creating a new one
      isLiveChat: (id) =>
        stores.threads.getById(id)
          ? isLiveThread(stores.threads, id)
          : this.cardThreadLoads.get(id) !== 'missing',
    });
  }

  private loadCardThread(
    threadId: string,
    stores: ReturnType<typeof getStores>
  ) {
    if (this.cardThreadLoads.has(threadId)) return;
    this.cardThreadLoads.set(threadId, 'loading');

    stores.threads.ensureThreadIsReady(threadId).then(
      (thread) =>
        runInAction(() => {
          if (thread) this.cardThreadLoads.delete(threadId);
          else this.cardThreadLoads.set(threadId, 'missing');

          this.syncActiveSessionToTarget({
            createDraft: this.createsDraftOnTargetChange,
          });
        }),
      (error: unknown) => {
        // Nothing re-syncs after a failed load, so the chat stays closed; say why in the log
        logger.warn('Context chat could not load a card thread', {
          threadId,
          error: error instanceof Error ? error.message : String(error),
        });
        runInAction(() => {
          this.cardThreadLoads.delete(threadId);
        });
      }
    );
  }

  private createCardThread(
    cardId: string,
    stores: ReturnType<typeof getStores>
  ): string {
    const threadId = stores.threads.createEmptyThread().id;

    const card = stores.actionCards.get(cardId);
    // A pending ask sends its prompt with the card attached, so the composer stays empty for the next message
    const asking = this.pendingCardAsk?.cardId === cardId;
    const seed = card && !asking ? appendActionCardChip(undefined, card) : null;
    if (seed) void stores.drafts.updateDraft({ draftId: threadId, ...seed });

    return threadId;
  }

  private leaveSession(
    session: ContextChatSession,
    stores: ReturnType<typeof getStores>
  ) {
    const thread = stores.threads.getById(session.threadId);
    const draft = stores.drafts.getDraft(session.threadId);
    if (
      session.context.type === 'action-card' &&
      !thread?.synced &&
      !thread?.lastMessageId &&
      draft &&
      isDraftEmpty({ ...draft, content: composerBody(draft) })
    )
      stores.drafts.removeDraft(draft);

    if (stores.threads.isFreshDraftThread(session.threadId))
      this.closeSession(session.id);
  }

  private applyPendingAsk(target: MeetingChatTarget) {
    if (!this.pendingAsk) return;

    if (this.pendingAsk.meetingId !== target.meetingId) {
      this.pendingAsk = null;
      return;
    }

    const session = this.rawActiveSession;
    if (!session || !this.sessionMatchesTarget(session, target)) return;

    if (this.mode === 'collapsed') this.mode = this.expandedMode;

    const { content, isMeetingIntent } = this.pendingAsk;
    this.pendingAsk = null;
    if (content === null) return;
    if (isMeetingIntent) {
      void this.sendToMeetingThread(session.id, session.threadId, content);
    } else {
      void this.sendPendingMessage(session.id, session.threadId, content);
    }
  }

  private applyPendingCardAsk(target: ActionCardChatTarget) {
    const ask = this.pendingCardAsk;
    if (!ask) return;

    if (ask.cardId !== target.cardId) {
      this.pendingCardAsk = null;
      this.askedCardIds.delete(ask.cardId);
      return;
    }

    const session = this.rawActiveSession;
    if (!session || !this.sessionMatchesTarget(session, target)) return;

    this.pendingCardAsk = null;
    void this.sendCardAsk(session.threadId, ask);
  }

  // Sent directly, not through the composer draft, so the user's unsent text stays
  private async sendCardAsk(
    threadId: string,
    { cardId, content }: { cardId: string; content: string }
  ) {
    try {
      const dispatch = await performAttributedSend({
        threadId,
        mode: MAIN_CHAT_MODE,
        requestResponseAudio: false,
        content,
        prepareExtras: () => ({ followUpActionCardIds: [cardId] }),
      });
      if (dispatch.createdThreadId === null) await dispatch.sendPromise;
    } catch (error) {
      runInAction(() => this.askedCardIds.delete(cardId));
      logError(error, 'Failed to ask about a card in its chat', {
        context: 'ContextChatStore:sendCardAsk',
        metadata: { cardId },
      });
    }
  }

  private sessionMatchesTarget(
    session: ContextChatSession,
    target: ChatTarget
  ): boolean {
    if (target.type === 'general') return session.context.type === 'general';
    if (target.type === 'action-card')
      return (
        session.context.type === 'action-card' &&
        session.context.cardId === target.cardId
      );
    if (session.context.type !== 'meeting') return false;
    return session.context.meetingId === target.meetingId;
  }

  private latestAssistantIdForThread(threadId: string): string | null {
    let stores: ReturnType<typeof getStores>;

    try {
      stores = getStores();
    } catch {
      return null;
    }

    const streamingId =
      stores.messages.getStreamingMessageForThread(threadId)?.id;
    const messages = stores.messages.getMessagesByThreadId(threadId);

    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role !== 'user' && messages[i].id !== streamingId)
        return messages[i].id;
    }

    return null;
  }

  private latestAssistantIdForFocusedSession(): string | null {
    const focused = this.focusedSession;

    if (!focused?.threadId) return null;

    let stores: ReturnType<typeof getStores>;
    try {
      stores = getStores();
    } catch {
      return null;
    }

    const streamingId = stores.messages.getStreamingMessageForThread(
      focused.threadId
    )?.id;
    const messages = stores.messages.getMessagesByThreadId(focused.threadId);
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role !== 'user' && messages[i].id !== streamingId)
        return messages[i].id;
    }

    return null;
  }

  get focusedSession(): ContextChatSession | undefined {
    if (!this.panelOpen) return undefined;
    return this.activeSession;
  }

  isThreadStreaming(threadId: string) {
    let stores: ReturnType<typeof getStores>;

    try {
      stores = getStores();
    } catch {
      return false;
    }

    return !!stores.messages.getStreamingMessageForThread(threadId);
  }

  sessionHasUnread(sessionId: string) {
    const session = this.sessions.find((s) => s.id === sessionId);

    if (!session?.threadId) return false;
    if (this.focusedSession?.id === sessionId) return false;

    let stores: ReturnType<typeof getStores>;
    try {
      stores = getStores();
    } catch {
      return false;
    }

    if (stores.messages.getStreamingMessageForThread(session.threadId))
      return false;

    const messages = stores.messages.getMessagesByThreadId(session.threadId);
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role !== 'user') {
        return messages[i].id !== (session.lastSeenAssistantId ?? null);
      }
    }

    return false;
  }

  private pruneDeletedSessions() {
    if (this.sessions.length === 0) return;

    let stores: ReturnType<typeof getStores>;
    try {
      stores = getStores();
    } catch {
      return;
    }

    if (!stores.threads.isLoaded) return;

    const next = this.sessions.filter((session) =>
      isLiveThread(stores.threads, session.threadId)
    );

    if (next.length === this.sessions.length) return;
    this.sessions = next;

    if (
      this.activeSessionId &&
      !next.some((s) => s.id === this.activeSessionId)
    ) {
      this.activeSessionId = null;
      this.collapse();
      this.syncActiveSessionToTarget({ createDraft: true });
    }
  }

  setCurrentTarget(target: ChatTarget | undefined) {
    this.currentTarget = target;
  }

  private get rawActiveSession(): ContextChatSession | undefined {
    return this.sessions.find((s) => s.id === this.activeSessionId);
  }

  get activeSession(): ContextChatSession | undefined {
    if (!this.currentTarget) return undefined;

    const session = this.rawActiveSession;
    if (!session) return undefined;
    return this.sessionMatchesTarget(session, this.currentTarget)
      ? session
      : undefined;
  }

  expand() {
    this.syncActiveSessionToTarget({ createDraft: true });
    this.mode = this.activeThreadHasMessages() ? this.expandedMode : 'composer';
  }

  private get expandedMode(): ContextChatMode {
    if (this.composerOnly) return 'composer';
    return (this.preferDocked || this.dockOnly) && this.canDock
      ? 'docked'
      : 'floating';
  }

  private openExpanded() {
    this.mode = this.expandedMode;
    this.syncActiveSessionToTarget({ createDraft: true });
  }

  private activeThreadHasMessages(): boolean {
    const session = this.activeSession;
    if (!session) return false;

    try {
      const { messages } = getStores();
      return messages.getMessagesByThreadId(session.threadId).length > 0;
    } catch {
      return false;
    }
  }

  dock() {
    this.mode = 'docked';
    // A page that draws the chat itself is not the dock, so the user's saved layout stays
    if (!this.dockOnly && !this.embedded) this.preferDocked = true;
    this.syncActiveSessionToTarget({ createDraft: true });
  }

  float() {
    this.mode = 'floating';
    this.syncActiveSessionToTarget({ createDraft: true });
  }

  undock() {
    this.preferDocked = false;
    this.float();
  }

  setCanDock(value: boolean) {
    this.canDock = value;
  }

  setEmbedded(value: boolean) {
    this.embedded = value;
  }

  setDockOnly(value: boolean) {
    this.dockOnly = value;
  }

  collapse() {
    this.mode = 'collapsed';
    this.pendingAsk = null;
    this.collapsedByUser = true;
    this.restoreAfterNavigation = false;
  }

  /**
   * Screen-lifecycle collapse, as opposed to a user one: an open panel is
   * restored on the next target sync, so a mount/cleanup cycle can't shut it.
   */
  collapseForNavigation() {
    // A chat a page drew inside itself leaves with the page, so no other page reopens it
    this.restoreAfterNavigation ||= this.panelOpen && !this.embedded;
    this.mode = 'collapsed';
  }

  close() {
    this.collapse();
  }

  askAboutSelection(target: MeetingChatTarget, text: string) {
    if (!text.trim()) return;

    this.setCurrentTarget(target);
    this.openExpanded();

    const session = this.focusedSession;
    if (!session) return;

    let stores: ReturnType<typeof getStores>;
    try {
      stores = getStores();
    } catch {
      return;
    }

    stores.drafts.appendSelectedText(session.threadId, text);
  }

  openForMeeting(meetingId: string) {
    this.pendingAsk = { meetingId, content: null };
    this.setCurrentTarget({ type: 'meeting', meetingId });
    this.openExpanded();
  }

  askAboutCard(cardId: string, content: string) {
    if (!this.askedCardIds.has(cardId)) {
      this.askedCardIds.add(cardId);
      this.pendingCardAsk = { cardId, content };
    }
    this.setCurrentTarget({ type: 'action-card', cardId });
    this.dock();
  }

  askQuestion(target: MeetingChatTarget, message: string) {
    const content = message.trim();
    if (!content) return;

    this.pendingAsk = { meetingId: target.meetingId, content };
    this.syncActiveSessionToTarget({ createDraft: true, target });
  }

  askMeetingIntent(target: MeetingChatTarget, prompt: string) {
    this.pendingAsk = {
      meetingId: target.meetingId,
      content: prompt,
      isMeetingIntent: true,
    };
    this.syncActiveSessionToTarget({ createDraft: true, target });
  }

  private async sendPendingMessage(
    sessionId: string,
    threadId: string,
    content: string
  ) {
    let stores: ReturnType<typeof getStores>;
    try {
      stores = getStores();
    } catch {
      return;
    }

    await stores.drafts.updateDraft({ draftId: threadId, content });

    await this.sendToMeetingThread(sessionId, threadId);
  }

  private async sendToMeetingThread(
    sessionId: string,
    threadId: string,
    content?: string
  ) {
    let stores: ReturnType<typeof getStores>;
    try {
      stores = getStores();
    } catch {
      return;
    }

    if (!stores.threads.getById(threadId)) return;

    const dispatch = await performAttributedSend({
      threadId,
      mode: MAIN_CHAT_MODE,
      requestResponseAudio: false,
      content,
      prepareExtras: (sendThreadId) => ({ draftId: sendThreadId }),
      onMeetingThreadPrepared: (preparedThreadId) =>
        this.attachThread(sessionId, preparedThreadId),
    });
    if (dispatch.createdThreadId === null) {
      await dispatch.sendPromise;
    }
  }

  async startNewGeneralChat() {
    const session = this.rawActiveSession;
    if (session?.context.type !== 'general') return;
    if (this.isSwitchingGeneralThread) return;

    let stores: ReturnType<typeof getStores>;
    try {
      stores = getStores();
    } catch {
      return;
    }

    const sessionId = session.id;
    const previousThreadId = session.threadId;
    const newThreadId = stores.threads.createEmptyThread({
      origin: 'meetings_overview',
    }).id;

    this.isSwitchingGeneralThread = true;
    let draftPreserved: boolean;
    try {
      draftPreserved = await this.preserveDraftForThreadSwitch(
        previousThreadId,
        newThreadId
      );
    } finally {
      runInAction(() => {
        this.isSwitchingGeneralThread = false;
      });
    }

    runInAction(() => {
      if (!draftPreserved) {
        stores.threads.discardFreshDraftThread(newThreadId);
        return;
      }

      const newSession: ContextChatSession = {
        id: uuidv4(),
        context: { type: 'general' },
        threadId: newThreadId,
        lastActiveAt: Date.now(),
        lastSeenAssistantId: null,
      };
      this.sessions = [
        ...this.sessions.filter((s) => s.id !== sessionId),
        newSession,
      ];
      this.activeSessionId = newSession.id;
      if (this.mode !== 'docked' && !this.dockOnly) this.mode = 'composer';
      this.discardLocalThreadIfOrphaned(previousThreadId);
    });
  }

  async openGeneralChat(threadId: string) {
    const session = this.rawActiveSession;
    if (session?.context.type !== 'general') return;

    if (session.threadId === threadId) {
      if (this.mode !== 'docked') this.mode = this.expandedMode;
      return;
    }
    if (this.isSwitchingGeneralThread) return;

    const sessionId = session.id;
    const previousThreadId = session.threadId;
    this.isSwitchingGeneralThread = true;
    let draftPreserved: boolean;
    try {
      draftPreserved = await this.preserveDraftForThreadSwitch(
        previousThreadId,
        threadId
      );
    } finally {
      runInAction(() => {
        this.isSwitchingGeneralThread = false;
      });
    }
    if (!draftPreserved) return;

    runInAction(() => {
      const currentSession = this.sessions.find(
        (item) => item.id === sessionId
      );
      if (
        this.activeSessionId !== sessionId ||
        currentSession?.context.type !== 'general' ||
        currentSession.threadId !== previousThreadId
      ) {
        return;
      }

      this.attachThread(sessionId, threadId);
      currentSession.lastSeenAssistantId =
        this.latestAssistantIdForThread(threadId);
      if (this.mode !== 'docked') this.mode = this.expandedMode;
    });
  }

  private async preserveDraftForThreadSwitch(
    fromThreadId: string,
    toThreadId: string
  ): Promise<boolean> {
    let stores: ReturnType<typeof getStores>;
    try {
      stores = getStores();
    } catch {
      return false;
    }

    const pendingDraft = stores.drafts.getDraft(fromThreadId);
    if (!pendingDraft || isDraftEmpty(pendingDraft)) return true;

    const targetDraft = stores.drafts.getDraft(toThreadId);
    if (targetDraft && !isDraftEmpty(targetDraft)) return false;

    await stores.drafts.moveDraft(fromThreadId, toThreadId);
    return true;
  }

  private closeSession(sessionId: string) {
    const session = this.sessions.find((s) => s.id === sessionId);
    this.sessions = this.sessions.filter((s) => s.id !== sessionId);

    if (this.activeSessionId === sessionId) {
      this.activeSessionId = null;
      this.collapse();
    }

    if (session?.threadId) this.discardLocalThreadIfOrphaned(session.threadId);
  }

  private discardLocalThreadIfOrphaned(threadId: string) {
    let stores: ReturnType<typeof getStores>;
    try {
      stores = getStores();
    } catch {
      return;
    }

    if (this.sessions.some((s) => s.threadId === threadId)) return;

    stores.threads.discardFreshDraftThread(threadId);
  }

  attachThread(sessionId: string, threadId: string) {
    const session = this.sessions.find((s) => s.id === sessionId);
    if (!session) return;

    const previousThreadId = session.threadId;
    session.threadId = threadId;
    if (previousThreadId !== threadId) {
      this.discardLocalThreadIfOrphaned(previousThreadId);
    }
  }

  setPendingContextHash(
    sessionId: string,
    hash: string | undefined,
    baselineLastMessageId: string | null
  ) {
    const session = this.sessions.find((s) => s.id === sessionId);
    if (!session) return;

    session.pendingContextHash = hash;
    session.pendingContextBaselineLastMessageId = hash
      ? baselineLastMessageId
      : undefined;
  }

  /**
   * Promotes the session's pending context hash to its last-sent
   * hash. Called once an assistant reply lands so we can skip the
   * (now redundant) context payload on the next turn.
   */
  commitPendingContextHash(sessionId: string) {
    const session = this.sessions.find((s) => s.id === sessionId);
    if (!session?.pendingContextHash) return;
    session.lastSentContextHash = session.pendingContextHash;
    session.pendingContextHash = undefined;
    session.pendingContextBaselineLastMessageId = undefined;
  }

  setSidebarWidth(px: number) {
    this.sidebarWidth = Math.max(
      sidebarConfig.minWidth,
      Math.min(sidebarConfig.maxWidth, px)
    );
  }

  isResizingSidebar = false;
  setResizingSidebar(value: boolean) {
    this.isResizingSidebar = value;
  }

  setFloatingHeight(px: number) {
    this.floatingHeight = Math.max(
      floatingConfig.minHeight,
      Math.min(floatingConfig.maxHeight, px)
    );
  }

  isResizingFloating = false;
  setResizingFloating(value: boolean) {
    this.isResizingFloating = value;
  }

  private hydrate() {
    try {
      const raw = localStorage.getItem(storageKeys.sidebar);
      if (raw) {
        const parsed = JSON.parse(raw) as {
          sidebarWidth?: number;
          floatingHeight?: number;
          mode?: ContextChatMode;
          preferDocked?: boolean;
        };
        if (typeof parsed.sidebarWidth === 'number') {
          this.sidebarWidth = Math.max(
            sidebarConfig.minWidth,
            Math.min(sidebarConfig.maxWidth, parsed.sidebarWidth)
          );
        }
        if (typeof parsed.floatingHeight === 'number') {
          this.floatingHeight = Math.max(
            floatingConfig.minHeight,
            Math.min(floatingConfig.maxHeight, parsed.floatingHeight)
          );
        }
        if (typeof parsed.preferDocked === 'boolean') {
          this.preferDocked = parsed.preferDocked;
        } else if (parsed.mode === 'docked') {
          this.preferDocked = true;
        }
      }
    } catch {
      // ignore
    }
  }

  private persistChatLayout() {
    // Read inside the autorun so MobX tracks these as deps; the resize
    // drags suppress the actual write so we don't hammer localStorage on
    // every mousemove. The flip back to false at pointer-up re-runs this
    // and writes the final width/height.
    const sidebarWidth = this.sidebarWidth;
    const floatingHeight = this.floatingHeight;
    const preferDocked = this.preferDocked;
    if (this.isResizingSidebar || this.isResizingFloating) return;

    try {
      localStorage.setItem(
        storageKeys.sidebar,
        JSON.stringify({ sidebarWidth, floatingHeight, preferDocked })
      );
    } catch {
      // best-effort
    }
  }
}

export const contextChatStore = new ContextChatStore();
